import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import { PopperPlacementType } from "@mui/base/Popper";
import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  deleteConversationTag,
  patchConversationTag,
  postConversationTag,
} from "@redotech/redo-merchant-app-common/client/conversation-tags";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ConversationTagWithId } from "@redotech/redo-model/conversation";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  RedoList,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { Divider } from "@redotech/redo-web/divider";
import { Dropdown } from "@redotech/redo-web/dropdown";
import { Flex } from "@redotech/redo-web/flex";
import classNames from "classnames";
import {
  Dispatch,
  memo,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getConversation } from "../client/conversations";
import { ActiveConversationContext } from "../context/active-conversation-context";
import { ConversationTagsContext } from "../services/conversation-tags-service";
import * as conversationTagInputCss from "./conversation-tag-input.module.css";
import { CreateTagModal } from "./create-tag-modal";
import { EditTagModal } from "./edit-tag-modal";
import { GroupedConversationTags } from "./grouped-conversation-tags";
import {
  createGroupedTagListItems,
  createTagGroupMaps,
} from "./tag-grouping-utils";

export const ConversationTagInput = memo(function ConversationTagInput({
  currentTags,
  setCurrentTags,
  disabled = false,
  onlyDropdownMode = undefined,
  showAddButtonToEnterInput: showAddButtonToEnterInput = false,
  dropdownPlacement = "bottom",
  addButtonLabel = "Add tag",
}: {
  currentTags: ConversationTagWithId[];
  setCurrentTags(tags: ConversationTagWithId[]): void;
  disabled?: boolean;
  showBorder?: boolean;
  onlyDropdownMode?: {
    anchor: HTMLElement | null;
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
  };
  showAddButtonToEnterInput?: boolean;
  dropdownPlacement?: PopperPlacementType;
  addButtonLabel?: string;
}) {
  const [availableTags, setAvailableTags] = useState<ConversationTagWithId[]>(
    [],
  );
  const team = useRequiredContext(TeamContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const canCreateTags = useHasPermission(SupportPermissions.MANAGE_TAG);

  const tagGroupsResult = useLoad(async () => {
    const response = await rpcClient.getTagGroups({});
    return response.tagGroups;
  }, [rpcClient]);
  const [conversationTagsLoad, reloadConversationTags] = useLazyContext(
    ConversationTagsContext,
  );

  const { setActiveConversation, activeConversation } = useContext(
    ActiveConversationContext,
  );

  const [conversationTags] = useLazyContext(ConversationTagsContext);

  useEffect(() => {
    setAvailableTags(conversationTags.value || []);
  }, [conversationTags]);

  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  const selectedTags: string[] = useMemo(() => {
    return currentTags.map((tag) => tag.tagId);
  }, [currentTags]);

  const [searchText, setSearchText] = useState<string>("");

  const [selectDropdownOpen_, setSelectDropdownOpen_] = useState(false);

  const [selectDropdownOpen, setSelectDropdownOpen] = onlyDropdownMode
    ? [onlyDropdownMode.open, onlyDropdownMode.setOpen]
    : [selectDropdownOpen_, setSelectDropdownOpen_];

  const [tagBeingEdited, setTagBeingEdited] = useState<
    ConversationTagWithId | undefined
  >(undefined);

  const [createTagModalOpen, setCreateTagModalOpen] = useState(false);

  const filteredAvailableTags = useMemo(() => {
    if (!searchText) {
      return availableTags;
    }
    const lower = searchText.toLowerCase();
    return availableTags.filter((tag) =>
      tag.name.toLowerCase().includes(lower),
    );
  }, [availableTags, searchText]);

  function addTagToConversation(tag: ConversationTagWithId) {
    if (currentTags.some((usedTag) => usedTag.name === tag.name)) {
      return;
    }

    let newTags = [...currentTags];

    // Handle allowMultipleTags constraint
    if (tagGroupsResult.value) {
      const { tagToGroupMap } = createTagGroupMaps(
        tagGroupsResult.value,
        availableTags,
      );
      const groupOfNewTag = tagToGroupMap.get(tag.tagId);

      // If the new tag is in a group that doesn't allow multiple tags
      if (groupOfNewTag && !groupOfNewTag.allowMultipleTags) {
        // Remove any existing tags from the same group
        newTags = newTags.filter((currentTag) => {
          const groupOfCurrentTag = tagToGroupMap.get(currentTag.tagId);
          return (
            !groupOfCurrentTag || groupOfCurrentTag._id !== groupOfNewTag._id
          );
        });
      }
    }

    // Add the new tag
    newTags.push(tag);
    setCurrentTags(newTags);
    setSearchText("");
  }

  function removeTagFromConversation(tag: ConversationTagWithId) {
    // Eagerly update
    const updatedTags = currentTags.filter(
      (current) => current.name !== tag.name,
    );
    setCurrentTags(updatedTags);
  }

  const addNewTagButton = (
    <Flex
      className={conversationTagInputCss.pointer}
      onClick={() => setSelectDropdownOpen(true)}
    >
      <RedoBadge
        color="gray"
        dataTestId="add-tag-button"
        segmentLeading={{ type: "icon", Icon: PlusIcon }}
        size="xs"
        text={currentTags.length === 0 ? addButtonLabel : undefined}
      />
    </Flex>
  );

  const existingTags = (
    <GroupedConversationTags
      addButton={showAddButtonToEnterInput ? addNewTagButton : undefined}
      currentTags={currentTags}
      disabled={disabled}
      onTagRemoved={removeTagFromConversation}
    />
  );

  useEffect(() => {
    if (selectDropdownOpen) {
      inputRef?.focus();
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectDropdownOpen]);

  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

  const tagInput = (
    <input
      autoFocus
      className={classNames(
        conversationTagInputCss.input,
        !selectDropdownOpen &&
          showAddButtonToEnterInput &&
          conversationTagInputCss.hide,
      )}
      disabled={disabled}
      onChange={(event) => setSearchText(event.target.value)}
      onClick={(event) => {
        event.stopPropagation();
        setSelectDropdownOpen(true);
      }}
      onFocus={(event) => {
        event.stopPropagation();
        setSelectDropdownOpen(true);
      }}
      placeholder="Search tags..."
      ref={setInputRef}
      value={searchText}
    />
  );

  const wrappedInput = (
    <Flex className={conversationTagInputCss.inputFlex} ref={setDropdownAnchor}>
      <div className={conversationTagInputCss.inputWrapper}>{existingTags}</div>
    </Flex>
  );

  const tagItems: RedoListItem<ConversationTagWithId | "create">[] =
    useMemo(() => {
      const createTagItem = canCreateTags
        ? {
            id: "create",
            value: "create" as const,
            type: "text" as const,
            text: "New tag",
            leadingItem: { type: "icon" as const, Icon: PlusIcon },
          }
        : undefined;

      return createGroupedTagListItems(
        filteredAvailableTags,
        tagGroupsResult.value,
        {
          createTagMenuItem: createTagItem,
          onTagEdit: (tag) => {
            setTagBeingEdited(tag);
            setSelectDropdownOpen(false);
          },
          includeCreateValue: true,
        },
      );
    }, [
      filteredAvailableTags,
      canCreateTags,
      setSelectDropdownOpen,
      tagGroupsResult.value,
    ]);

  const [focusedIndex, setFocusedIndex] = useState<number | undefined>();

  const handleItemSelected = useHandler(
    ({
      item,
      selected,
    }: {
      item: RedoListItem<ConversationTagWithId | "create">;
      selected: boolean;
    }) => {
      if (item.type === "section") {
        return;
      }

      if (item.value === "create") {
        setCreateTagModalOpen(true);
        setSelectDropdownOpen(false);
      } else {
        const tag = item.value;
        if (selected) {
          addTagToConversation(tag);
        } else {
          removeTagFromConversation(tag);
        }
      }
    },
  );

  const dropdownItems = (
    <Dropdown
      anchor={onlyDropdownMode?.anchor || dropdownAnchor}
      fitToAnchor={false}
      open={selectDropdownOpen}
      placement={dropdownPlacement}
    >
      <Flex
        className={conversationTagInputCss.dropdownContainer}
        dir="column"
        gap="sm"
      >
        {tagInput}
        <Divider />
        <RedoList
          focusedIndex={focusedIndex}
          items={tagItems}
          itemSelected={handleItemSelected}
          itemsLoading={conversationTags.pending}
          refToListenTo={inputRef}
          selectedItems={selectedTags}
          setFocusedIndex={setFocusedIndex}
          size="sm"
        />
      </Flex>
    </Dropdown>
  );

  const dropdown = selectDropdownOpen && (
    <ClickAwayListener onClickAway={() => setSelectDropdownOpen(false)}>
      {dropdownItems}
    </ClickAwayListener>
  );

  async function handleDelete({ tagId }: { tagId: string }) {
    await deleteConversationTag(client, tagId, async () => {
      reloadConversationTags();
      if (activeConversation) {
        const updatedConversation = await getConversation(client, {
          conversationId: activeConversation._id,
        });
        setActiveConversation(updatedConversation);
      }
    });
    setTagBeingEdited(undefined);
    setSelectDropdownOpen(true);
  }

  async function handleEdit({
    tagId,
    name,
    pillTheme,
  }: {
    tagId: string;
    name: string;
    pillTheme: PillTheme;
  }) {
    await patchConversationTag(client, tagId, { name, pillTheme }, async () => {
      reloadConversationTags();
      if (activeConversation) {
        const updatedConversation = await getConversation(client, {
          conversationId: activeConversation._id,
        });
        setActiveConversation(updatedConversation);
      }
    });
  }

  const editModal = tagBeingEdited && (
    <EditTagModal
      handleDelete={handleDelete}
      handleEdit={handleEdit}
      resolved={() => {
        setTagBeingEdited(undefined);
        setSelectDropdownOpen(true);
      }}
      tag={tagBeingEdited}
      tags={conversationTags}
    />
  );

  async function handleSubmit({
    name,
    pillTheme,
    tagGroupId,
  }: {
    name: string;
    pillTheme: PillTheme;
    tagGroupId?: string | null;
  }) {
    const newTag = await postConversationTag(
      client,
      { name, pillTheme, teamId: team._id },
      reloadConversationTags,
    );

    if (tagGroupId) {
      try {
        const targetGroup = tagGroupsResult.value?.find(
          (group) => group._id === tagGroupId,
        );
        if (targetGroup) {
          await rpcClient.updateTagGroup({
            _id: targetGroup._id,
            tagsToAdd: [newTag._id],
          });
        }
      } catch (error) {
        console.error("Failed to add tag to group:", error);
      }
    }

    const newTagTyped: ConversationTagWithId = {
      name: newTag.name,
      pillTheme: newTag.pillTheme,
      tagId: newTag._id,
      source: newTag.source,
    };
    setSelectDropdownOpen(true);
    setCreateTagModalOpen(false);
    setAvailableTags([...availableTags, newTagTyped]);
  }

  const createModal = createTagModalOpen && (
    <CreateTagModal
      cancelClicked={() => {
        setSelectDropdownOpen(true);
        setCreateTagModalOpen(false);
      }}
      handleCreateTag={({
        name,
        pillTheme,
        tagGroupId,
      }: {
        name: string;
        pillTheme: PillTheme;
        tagGroupId?: string | null;
      }) => handleSubmit({ name, pillTheme, tagGroupId })}
      tagGroups={tagGroupsResult.value || []}
      tagsLoad={conversationTagsLoad}
    />
  );

  return (
    <>
      {!onlyDropdownMode ? (
        <div className={conversationTagInputCss.fullWidth}>
          {wrappedInput}
          {dropdown}
          {editModal}
          {createModal}
        </div>
      ) : (
        <>
          {dropdown}
          {editModal}
          {createModal}
        </>
      )}
    </>
  );
});
